import React, { useState } from "react"
import { Stack, Typography, AppBar, Box, Drawer, Divider, Button, Slider } from "@mui/material"
import lsLogo from '../Assets/mg-ls-web-flav.png'
import { useLSContext } from "../Context/useLSContext";
import { IOSSwitch } from "./iosSwitch";
import { useNavigate, useLocation } from "react-router-dom";
import { useDataContext } from "../Context/useDataContext";
import { useAuth } from "react-oidc-context";
import MenuIcon from '@mui/icons-material/Menu';
import { ResetSessionPopup } from "./ResetSessionPopup";
import { EndOfRoundPopup } from "./EndOfRoundPopup";
import { RoundCompleteFollowUp } from "./RoundCompleteFollowUp";
import { GroupScorerConfirmation } from "./GroupScorerConfirmation";
import { EventSelectionWindow } from "./EventSelectionWindow";
import { ToolsDrawer } from "./ToolsDrawer";
import { InfoDrawer } from "./InfoDrawer";
import { PopupMessageWrapper } from "./PopupMessageWrapper";

const menuHeaderFlag = `${process.env.PUBLIC_URL}/Quilted Flag - Bold Zoom Tilt.png`;
const menuHeaderCursive = `${process.env.PUBLIC_URL}/Stacked Cursive.png`;

interface LSHeaderProps {
    onRestart?: () => void;
}

export const LSHeader = ({ onRestart }: LSHeaderProps) => {

    const navigate = useNavigate();
    const location = useLocation();
    const auth = useAuth();
    const {
        factor, setFactor,
    } = useLSContext();

    const { eventData, adminMode, setAdminMode, userData, currentRound } = useDataContext();

    const [menuOpen, setMenuOpen] = useState(false);
    const [resetOpen, setResetOpen] = useState(false);
    const [localFactor, setLocalFactor] = useState(1);
    const [testRoundComplete, setTestRoundComplete] = useState(false);
    const [testRoundFollowUp, setTestRoundFollowUp] = useState(false);
    const [testGroupScorer, setTestGroupScorer] = useState(false);
    const [testMissingScores, setTestMissingScores] = useState(false);
    const mockScoreCheck = [
        {
            _id: 'preview-marker',
            markerScoreCode: 'PREVIEW',
            scoreCode: 'OPP',
            playerName: 'Player 1',
            incompletePlayerHoles: [3, 7],
            incompleteMarkerHoles: [12],
        },
    ];
    const [previewScoreCheck, setPreviewScoreCheck] = useState(mockScoreCheck);
    const [testEventSelection, setTestEventSelection] = useState(false);
    const [testToolsOpen, setTestToolsOpen] = useState(false);
    const [testInfoOpen, setTestInfoOpen] = useState(false);

    const isLeaderboardRoute = location.pathname.includes('leaderboard');
    const defaultTitle = isLeaderboardRoute ? 'Leaderboard' : 'Live Scoring';
    const eventTitle =
        typeof eventData?.eventName === 'string' && eventData.eventName.trim().length > 0
            ? eventData.eventName.trim()
            : defaultTitle;

    const signOutRedirect = () => {
      const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
      const logoutUri = window.location.origin;
      const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN;
      window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
  };

    return (
        <AppBar id='live-scoring-header-ab' sx={{ height: 60, backgroundColor: 'primary.main', padding: '3px 20px', zIndex: 200
          , alignItems: 'flex-start', justifyContent: 'center'
         }}>
            <Stack
                direction="row"
                spacing={1}
                width="100%"
                justifyContent="space-between"
                alignItems="stretch"
                sx={{ minHeight: `calc(60px - 6px)` }}
            > 
                {/* <img src={flagLogo} alt="Home" 
                  style={{ height: 50 *(factor < 1 ? factor : 1)}} 
                  /> */}
                <Box sx={{ width: 50, alignItems: 'center', justifyContent: 'center', display: 'flex', alignSelf: 'center', flexShrink: 0 }}>
                    <MenuIcon sx={{ color: 'secondary.main', fontSize: 35 }} 
                            onClick={() => {
                                setMenuOpen(true);
                            }}
                        />
                </Box>
                {/* <Typography variant={windowSize.width > 1200 ? 'h5' : 'h6'} textAlign={'left'} sx={{ fontWeight: 500 }} color='secondary.main'>Moment Golf</Typography>
                 */}

                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        alignSelf: 'stretch',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        px: 0.5,
                    }}
                >
                    <Typography
                        component="span"
                        sx={{
                            fontWeight: 500,
                            color: 'secondary.main',
                            textAlign: 'center',
                            width: '100%',
                            maxHeight: '100%',
                            /** Max ~twice lines at ~lh 1.1 within ~52px usable bar inset (60 − vertical padding − alignment). */
                            fontSize: 'clamp(12px, 2vw + 2.95vmin + 0.375rem, 22px)',
                            lineHeight: 1.11,
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 2,
                            overflow: 'hidden',
                            wordBreak: 'break-word',
                        }}
                    >
                        {eventTitle}
                    </Typography>
                </Box>

                <Box sx={{ width: 50, alignItems: 'center', justifyContent: 'center', display: 'flex', alignSelf: 'center', flexShrink: 0 }}>
                    <img src={lsLogo} alt="Live Scoring" 
                        style={{ height: 50 * (localFactor < 1 ? localFactor : 1)}} 
                    /> 
                </Box>

                {menuOpen &&
                  <Drawer anchor='left' variant='temporary' open={menuOpen} onClose={() => setMenuOpen(false)}
                      sx={{
                          width: '60%',
                          maxWidth: '400px',
                          height: '100%',
                          '& .MuiDrawer-paper': {
                              width: '60%',
                              maxWidth: '400px',
                              boxSizing: 'border-box',
                              alignItems: 'center',
                              padding: 0,
                              backgroundColor: 'sepia.background',
                              borderRight: '1px solid',
                              borderColor: 'sepia.extraLight',
                          },
                      }}
                  >
                      <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}
                          sx={{ width: '100%', padding: '10px 5px 0px 5px', borderBottom: '3px solid', borderColor: 'rgba(143, 92, 43, 0.5)' }}>
                          <img src={menuHeaderCursive} alt="Moment Golf" style={{ height: 48, objectFit: 'contain', flex: 1, maxWidth: '60%' }} />
                          <img src={menuHeaderFlag} alt="" style={{ height: 48, objectFit: 'contain' }} />
                      </Stack>
                      <Typography sx={{fontSize: '1.25rem', fontWeight: 500, color: 'sepia.dark'}}>Live Scoring Menu</Typography>
                      <Stack  
                          direction='column' 
                          spacing={2} 
                          sx={{ 
                              //display: 'flex', 
                              alignItems: 'flex-start', 
                              justifyContent: 'space-between', 
                              width: '90%', 
                              padding: 1,
                              height: '100%',
                              marginBottom: '60px'
                          }}
                      >
                          <Stack direction='column' gap={'10px'}
                            sx={{
                                width: '100%',
                            }}
                          >
                              {currentRound > 0 && eventData?.rounds?.[currentRound - 1] && (
                                  <Stack
                                      sx={{
                                          width: '100%',
                                          px: 1,
                                          pb: 0.5,
                                          alignItems: 'flex-start',
                                      }}
                                      spacing={0.25}
                                  >
                                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'sepia.dark', width: '100%' }}>
                                          Round {currentRound}
                                      </Typography>
                                      <Typography
                                          sx={{
                                              fontSize: '0.875rem',
                                              fontWeight: 500,
                                              color: 'sepia.main',
                                              width: '100%',
                                          }}
                                      >
                                          {eventData.rounds[currentRound - 1]?.courseName ?? ''}
                                      </Typography>
                                  </Stack>
                              )}
                              <Button variant='text' onClick={() => 
                                  window.location.href = process.env.REACT_APP_MOMENT_CENTRAL_URL + 'myAccount'}
                              sx={{
                                  width: '100%',
                                  justifyContent: 'flex-start',
                                  textAlign: 'left',
                                  padding: 1,
                                  fontSize: '1rem',
                                  fontWeight: 500,
                                  color: 'sepia.main',
                              }}
                              >My Account</Button>
                              {/* <Button disabled={!allowChangeHoles || scoreLock} variant='text' color='primary' onClick={() => {
                                  setMenuOpen(false);
                                  setChangeHoleOpen(true);
                              }}
                              sx={{
                                  width: '100%',
                                  justifyContent: 'flex-start',
                                  textAlign: 'left',
                                  padding: 1
                              }}>Change Hole</Button> */}
                              <Button variant='text' onClick={() => {
                                  setMenuOpen(false);
                                  setResetOpen(true);
                              }}
                              sx={{
                                  width: '100%',
                                  justifyContent: 'flex-start',
                                  textAlign: 'left',
                                  padding: 1,
                                  fontSize: '1rem',
                                  fontWeight: 500,
                                  color: 'sepia.main',
                              }}>Home</Button>
                              {userData?.accountTier > 2 &&
                              <Stack direction='row' spacing={2} sx={{ alignItems: 'center', width: '100%', padding: 1 }}>
                                  <Typography sx={{ fontSize: '1rem', fontWeight: 500, color: 'sepia.main' }}>Admin Mode</Typography>
                                  <Typography sx={{ fontSize: '0.875rem', color: 'sepia.main' }}>Off</Typography>
                                  <IOSSwitch
                                      checked={adminMode}
                                      onChange={() => setAdminMode(!adminMode)}
                                  />
                                  <Typography sx={{ fontSize: '0.875rem', color: 'sepia.main' }}>On</Typography>
                              </Stack>}
                              {adminMode &&
                              <>
                              <Stack direction="column" spacing={1} sx={{ width: '100%'}}>
                                  <Typography sx={{fontSize: '1rem', fontWeight: 500, color: 'sepia.main'}}>App Zoom</Typography>
                                  <Stack direction="row" spacing={3} sx={{ width: '100%', alignItems: 'center' }}>
                                    <Typography sx={{fontSize: '1rem', fontWeight: 500, color: 'sepia.main'}}>1x</Typography>
                                        
                                    <Slider
                                        value={factor}
                                        onChange={(_, value) => setFactor(value)}
                                        step={0.25}
                                        min={1}
                                        max={2}
                                        marks
                                        valueLabelDisplay="auto"
                                        sx={{
                                            width: '100%',
                                            color: 'primary.main'
                                        }}
                                    />
                                    <Typography sx={{fontSize: '1rem', fontWeight: 500, color: 'sepia.main'}}>2x</Typography>
                                  </Stack>
                              </Stack>
                              <Divider sx={{ width: '100%', my: 1, borderColor: 'sepia.extraLight' }} />
                              <Typography sx={{ fontSize: '1rem', fontWeight: 500, color: 'sepia.main', width: '100%' }}>Test Popups</Typography>
                              <Stack direction="column" gap={0.5} sx={{ width: '100%' }}>
                                  <Button variant="text" size="small" onClick={() => { setMenuOpen(false); setResetOpen(true); }} sx={{ justifyContent: 'flex-start', color: 'sepia.main', fontSize: '0.875rem' }}>Return to Home</Button>
                                  <Button variant="text" size="small" onClick={() => { setMenuOpen(false); setTestRoundComplete(true); }} sx={{ justifyContent: 'flex-start', color: 'sepia.main', fontSize: '0.875rem' }}>Round Complete</Button>
                                  <Button variant="text" size="small" onClick={() => { setMenuOpen(false); setTestRoundFollowUp(true); }} sx={{ justifyContent: 'flex-start', color: 'sepia.main', fontSize: '0.875rem' }}>Round Follow Up</Button>
                                  <Button variant="text" size="small" onClick={() => { setMenuOpen(false); setTestGroupScorer(true); }} sx={{ justifyContent: 'flex-start', color: 'sepia.main', fontSize: '0.875rem' }}>Group Scorer</Button>
                                  <Button variant="text" size="small" onClick={() => { setMenuOpen(false); setTestMissingScores(true); }} sx={{ justifyContent: 'flex-start', color: 'sepia.main', fontSize: '0.875rem' }}>Missing Scores</Button>
                                  <Button variant="text" size="small" onClick={() => { setMenuOpen(false); setTestEventSelection(true); }} sx={{ justifyContent: 'flex-start', color: 'sepia.main', fontSize: '0.875rem' }}>Event Selection</Button>
                                  <Button variant="text" size="small" onClick={() => { setMenuOpen(false); setTestToolsOpen(true); }} sx={{ justifyContent: 'flex-start', color: 'sepia.main', fontSize: '0.875rem' }}>Tools Drawer</Button>
                                  <Button variant="text" size="small" onClick={() => { setMenuOpen(false); setTestInfoOpen(true); }} sx={{ justifyContent: 'flex-start', color: 'sepia.main', fontSize: '0.875rem' }}>Info Drawer</Button>
                              </Stack>
                              </>
                              }
                          </Stack>
                          <Stack direction='column' gap={'10px'} sx={{width: '100%'}}>
                              <Button variant='text' onClick={() => {
                                  setMenuOpen(false);
                              }}
                              sx={{
                                  width: '100%',
                                  justifyContent: 'flex-start',
                                  textAlign: 'left',
                                  padding: 1,
                                  fontSize: '1rem',
                                  fontWeight: 500,
                                  color: 'sepia.main',
                              }}
                              >Close Menu</Button>
                              <Button variant='text' onClick={() => {
                                  setMenuOpen(false);
                                  onRestart?.();
                                  navigate('/login');
                              }}
                              sx={{
                                  width: '100%',
                                  justifyContent: 'flex-start',
                                  textAlign: 'left',
                                  padding: 1,
                                fontSize: '0.875rem',
                                  fontWeight: 500,
                                  color: 'sepia.main',
                              }}>Restart App</Button>
                              
                              <Button variant='text' onClick={() => 
                                  signOutRedirect()}
                              sx={{
                                  width: '100%',
                                  justifyContent: 'flex-start',
                                  textAlign: 'left',
                                  padding: 1,
                                  fontSize: '0.875rem',
                                  fontWeight: 500,
                                  color: 'sepia.main',
                              }}
                              >Sign Out</Button>
                              {auth?.user &&
                                  <Stack direction='column' gap={'3px'} width='100%'>
                                      <Divider sx={{width: '100%', borderColor: 'sepia.extraLight'}}/>
                                      <Typography sx={{fontSize: '0.875rem', fontWeight: 500, color: 'sepia.dark'}}>{auth.user.profile.given_name} {auth.user.profile.family_name}</Typography>
                                      <Typography sx={{fontSize: '0.875rem', fontWeight: 500, color: 'sepia.main'}}>{auth.user.profile.email}</Typography>
                                  </Stack>
                              }
                          </Stack>
                          
                      </Stack>
                  </Drawer>
                }
              {
                resetOpen &&
                <ResetSessionPopup resetOpen={resetOpen} setResetOpen={setResetOpen} />
              }
              {testRoundComplete && (
                <EndOfRoundPopup
                  open={testRoundComplete}
                  onClose={() => setTestRoundComplete(false)}
                  initialPhase="confirm"
                  scoreCheck={[]}
                  setScoreCheck={() => {}}
                  newHoleSettings={() => {}}
                  setRoundFollowUp={() => {}}
                  preview
                />
              )}
              {testRoundFollowUp && (
                <RoundCompleteFollowUp
                  open={testRoundFollowUp}
                  setOpen={setTestRoundFollowUp}
                  newHoleSettings={() => {}}
                  preview
                />
              )}
              {testGroupScorer && (
                <GroupScorerConfirmation open={testGroupScorer} setOpen={setTestGroupScorer} preview />
              )}
              {testMissingScores && (
                <EndOfRoundPopup
                  open={testMissingScores}
                  onClose={() => setTestMissingScores(false)}
                  initialPhase="missing"
                  scoreCheck={previewScoreCheck}
                  setScoreCheck={setPreviewScoreCheck}
                  newHoleSettings={() => {}}
                  setRoundFollowUp={() => {}}
                  preview
                />
              )}
              {testEventSelection && (
                <EventSelectionWindow
                  open={testEventSelection}
                  setOpen={setTestEventSelection}
                  registrations={[]}
                  onSelect={() => setTestEventSelection(false)}
                />
              )}
              <ToolsDrawer open={testToolsOpen} setOpen={setTestToolsOpen} />
              <InfoDrawer open={testInfoOpen} setOpen={setTestInfoOpen} />
            </Stack>
        </AppBar>
    )
}
